/**
 * Settings → Tracks
 *
 * Workspace-level policy for how Tracks are defined when a new
 * cycle is created.
 *
 * Two policy modes:
 *   Category by Altitude  (default) — tracks auto-generated for every unique
 *                                      (Item Category × Altitude) combination
 *                                      present in a cycle's items.
 *   Custom                          — admin defines named tracks. Each custom
 *                                      track is defined one of two ways:
 *                                        • Dimensions — items sharing the same
 *                                          values across all dimensions are
 *                                          comparable (auto-grouped into lanes).
 *                                        • Conditions — an item is included when
 *                                          its fields satisfy 1..n conditions
 *                                          ("things that must be true"), joined
 *                                          by match=all (AND) or match=any (OR).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Layers,
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Info,
  Check,
  Pencil,
  X,
  MoveRight,
  Filter,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { isConditionComplete } from '../../../lib/baselineConditionEvaluator';
import type {
  BaselineTemplatePolicy,
  CustomBaselineDef,
  FrameDimension,
  FrameCondition,
  FrameConditionOperator,
  FrameDefinitionMode,
} from '../../../types/prioritization/tenant';

// ── Attribute metadata (drives operator + value-input choices) ────────────────

type AttributeType = 'text' | 'number' | 'select' | 'multiselect' | 'date' | 'boolean';

interface FrameAttributeMeta extends FrameDimension {
  type: AttributeType;
  options?: string[];
}

// System dimensions (always available, map to item row fields)
const SYSTEM_DIMENSIONS: FrameAttributeMeta[] = [
  { key: 'itemCategory', label: 'Item Category', type: 'select' },
  { key: 'altitude', label: 'Altitude', type: 'number' },
  { key: 'queue', label: 'Queue', type: 'select' },
  { key: 'source', label: 'Source', type: 'text' },
  { key: 'exit_status', label: 'Status', type: 'select', options: ['active', 'deferred', 'archived'] },
];

// ── Operator catalogue ────────────────────────────────────────────────────────

const OPERATOR_LABELS: Record<FrameConditionOperator, string> = {
  eq: 'is',
  neq: 'is not',
  is_set: 'is set',
  is_not_set: 'is not set',
  gt: 'greater than',
  gte: 'at least (≥)',
  lt: 'less than',
  lte: 'at most (≤)',
  between: 'between',
  in: 'is any of',
  not_in: 'is none of',
  contains: 'contains',
};

const PRESENCE_OPS: FrameConditionOperator[] = ['is_set', 'is_not_set'];

/** Operators offered for a given attribute type. */
function operatorsForType(type: AttributeType): FrameConditionOperator[] {
  switch (type) {
    case 'number':
    case 'date':
      return ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', ...PRESENCE_OPS];
    case 'select':
      return ['eq', 'neq', 'in', 'not_in', ...PRESENCE_OPS];
    case 'multiselect':
      return ['contains', 'in', 'not_in', ...PRESENCE_OPS];
    case 'boolean':
      return ['eq', ...PRESENCE_OPS];
    case 'text':
    default:
      return ['eq', 'neq', 'contains', ...PRESENCE_OPS];
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function generateId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function definitionModeOf(frame: CustomBaselineDef): FrameDefinitionMode {
  return frame.definitionMode ?? 'dimensions';
}

// ── Dimension chip ────────────────────────────────────────────────────────────

interface DimensionChipProps {
  dim: FrameDimension;
  index: number;
  total: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  readOnly?: boolean;
}

function DimensionChip({ dim, index, total, onMoveUp, onMoveDown, onRemove, readOnly }: DimensionChipProps) {
  return (
    <span className="inline-flex items-center gap-0 rounded-full border border-primary-200 bg-primary-50 text-primary-800 text-xs font-medium">
      {!readOnly && (
        <span className="flex flex-col border-r border-primary-200 px-1.5 py-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={index === 0}
            className="text-primary-400 hover:text-primary-700 disabled:opacity-25 disabled:cursor-not-allowed leading-none"
            aria-label="Move dimension left"
          >
            <ChevronUp className="h-2.5 w-2.5" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="text-primary-400 hover:text-primary-700 disabled:opacity-25 disabled:cursor-not-allowed leading-none"
            aria-label="Move dimension right"
          >
            <ChevronDown className="h-2.5 w-2.5" />
          </button>
        </span>
      )}
      <span className="px-2.5 py-1.5">{dim.label}</span>
      {!readOnly && (
        <button
          type="button"
          onClick={onRemove}
          className="mr-1 flex h-4 w-4 items-center justify-center rounded-full text-primary-500 hover:bg-primary-200 hover:text-primary-900"
          aria-label={`Remove ${dim.label}`}
        >
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </span>
  );
}

// ── Dimension picker ──────────────────────────────────────────────────────────

interface DimensionPickerProps {
  available: FrameAttributeMeta[];
  onAdd: (dim: FrameAttributeMeta) => void;
}

function DimensionPicker({ available, onAdd }: DimensionPickerProps) {
  const [selectedKey, setSelectedKey] = useState(available[0]?.key ?? '');

  useEffect(() => {
    if (!selectedKey && available.length > 0) setSelectedKey(available[0].key);
    if (selectedKey && !available.find((d) => d.key === selectedKey) && available.length > 0) {
      setSelectedKey(available[0].key);
    }
  }, [available, selectedKey]);

  const handleAdd = () => {
    const dim = available.find((d) => d.key === selectedKey);
    if (dim) onAdd(dim);
  };

  if (available.length === 0) {
    return <p className="text-xs text-[var(--text-ter)] italic">All available attributes are already in this frame.</p>;
  }

  return (
    <div className="flex items-end gap-2 flex-wrap">
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-[var(--text-sec)]">Attribute / field</label>
        <select
          value={selectedKey}
          onChange={(e) => setSelectedKey(e.target.value)}
          className="rounded border border-[var(--rule)] bg-[var(--bg-raised)] px-2.5 py-1.5 text-sm text-[var(--text-pri)] focus:border-primary-500 focus:ring-1 focus:ring-primary-500 min-w-[220px]"
        >
          {available.map((d) => (
            <option key={d.key} value={d.key}>{d.label}</option>
          ))}
        </select>
      </div>
      <button
        type="button"
        onClick={handleAdd}
        disabled={!selectedKey}
        className="mb-0.5 inline-flex items-center gap-1.5 rounded border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Plus className="h-3.5 w-3.5" />
        Add
      </button>
    </div>
  );
}

// ── Condition row ─────────────────────────────────────────────────────────────

interface ConditionRowProps {
  condition: FrameCondition;
  allDimensions: FrameAttributeMeta[];
  onChange: (next: FrameCondition) => void;
  onRemove: () => void;
}

function ConditionRow({ condition, allDimensions, onChange, onRemove }: ConditionRowProps) {
  const meta = allDimensions.find((d) => d.key === condition.key);
  const type = meta?.type ?? 'text';
  const operators = operatorsForType(type);

  const handleKeyChange = (key: string) => {
    const nextMeta = allDimensions.find((d) => d.key === key);
    const nextOps = operatorsForType(nextMeta?.type ?? 'text');
    // Reset operator if the current one isn't valid for the new attribute type.
    const operator = nextOps.includes(condition.operator) ? condition.operator : nextOps[0];
    onChange({
      ...condition,
      key,
      label: nextMeta?.label ?? key,
      operator,
      value: undefined,
      value2: undefined,
      values: undefined,
    });
  };

  const handleOperatorChange = (operator: FrameConditionOperator) => {
    onChange({ ...condition, operator, value: undefined, value2: undefined, values: undefined });
  };

  const needsNoValue = PRESENCE_OPS.includes(condition.operator);
  const isRange = condition.operator === 'between';
  const isList = condition.operator === 'in' || condition.operator === 'not_in';
  const inputType = type === 'number' ? 'number' : type === 'date' ? 'date' : 'text';

  return (
    <div className="flex flex-wrap items-center gap-2 border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2">
      {/* Attribute */}
      <select
        value={condition.key}
        onChange={(e) => handleKeyChange(e.target.value)}
        className="rounded border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5 text-sm text-[var(--text-pri)] focus:border-primary-500 focus:ring-1 focus:ring-primary-500 min-w-[170px]"
      >
        {allDimensions.map((d) => (
          <option key={d.key} value={d.key}>{d.label}</option>
        ))}
      </select>

      {/* Operator */}
      <select
        value={condition.operator}
        onChange={(e) => handleOperatorChange(e.target.value as FrameConditionOperator)}
        className="rounded border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5 text-sm text-[var(--text-pri)] focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
      >
        {operators.map((op) => (
          <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>
        ))}
      </select>

      {/* Value(s) */}
      {needsNoValue ? (
        <span className="text-xs text-[var(--text-ter)] italic">no value needed</span>
      ) : isList ? (
        <input
          type="text"
          value={(condition.values ?? []).join(', ')}
          onChange={(e) =>
            onChange({
              ...condition,
              values: e.target.value.split(',').map((v) => v.trim()).filter(Boolean),
            })
          }
          placeholder="comma, separated, values"
          className="flex-1 min-w-[160px] rounded border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5 text-sm text-[var(--text-pri)] focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
        />
      ) : isRange ? (
        <span className="inline-flex items-center gap-1.5">
          <input
            type={inputType}
            value={(condition.value as string | number | undefined) ?? ''}
            onChange={(e) => onChange({ ...condition, value: e.target.value })}
            placeholder="from"
            className="w-24 rounded border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5 text-sm text-[var(--text-pri)] focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
          />
          <span className="text-xs text-[var(--text-ter)]">and</span>
          <input
            type={inputType}
            value={condition.value2 ?? ''}
            onChange={(e) => onChange({ ...condition, value2: e.target.value })}
            placeholder="to"
            className="w-24 rounded border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5 text-sm text-[var(--text-pri)] focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
          />
        </span>
      ) : type === 'select' && meta?.options && meta.options.length > 0 ? (
        <select
          value={String(condition.value ?? '')}
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
          className="flex-1 min-w-[140px] rounded border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5 text-sm text-[var(--text-pri)] focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
        >
          <option value="">Select…</option>
          {meta.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      ) : type === 'boolean' ? (
        <select
          value={String(condition.value ?? '')}
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
          className="rounded border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5 text-sm text-[var(--text-pri)] focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
        >
          <option value="">Select…</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ) : (
        <input
          type={inputType}
          value={(condition.value as string | number | undefined) ?? ''}
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
          placeholder="value"
          className="flex-1 min-w-[140px] rounded border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5 text-sm text-[var(--text-pri)] focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
        />
      )}

      <button
        type="button"
        onClick={onRemove}
        className="ml-auto rounded p-1.5 text-[var(--text-ter)] hover:bg-[var(--coral-dim)] hover:text-[var(--coral)]"
        aria-label="Remove condition"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ── Human-readable condition summary (collapsed display) ──────────────────────

function conditionSummary(c: FrameCondition): string {
  const op = OPERATOR_LABELS[c.operator];
  if (PRESENCE_OPS.includes(c.operator)) return `${c.label} ${op}`;
  if (c.operator === 'between') return `${c.label} ${op} ${c.value ?? '?'}–${c.value2 ?? '?'}`;
  if (c.operator === 'in' || c.operator === 'not_in') return `${c.label} ${op} ${(c.values ?? []).join(', ') || '?'}`;
  return `${c.label} ${op} ${c.value ?? '?'}`;
}

// ── Frame row (collapsed display) ─────────────────────────────────────────────

interface FrameRowProps {
  frame: CustomBaselineDef;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
  isFirst: boolean;
  isLast: boolean;
}

function FrameRow({ frame, onEdit, onDelete, onMoveUp, onMoveDown, isFirst, isLast }: FrameRowProps) {
  const mode = definitionModeOf(frame);
  const conditions = frame.conditions ?? [];
  const joinWord = (frame.match ?? 'all') === 'any' ? 'OR' : 'AND';

  return (
    <div className="flex items-start gap-3 border border-[var(--rule)] bg-[var(--bg-raised)] px-4 py-3">
      {/* Reorder */}
      <div className="flex flex-col gap-0.5 shrink-0 mt-0.5">
        <button type="button" onClick={() => onMoveUp(frame.id)} disabled={isFirst}
          className="text-[var(--text-ter)] hover:text-[var(--text-ter)] disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Move up">
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => onMoveDown(frame.id)} disabled={isLast}
          className="text-[var(--text-ter)] hover:text-[var(--text-ter)] disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Move down">
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>
      <GripVertical className="h-4 w-4 text-[var(--text-ter)] shrink-0 mt-0.5" />

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-[var(--text-pri)]">{frame.name}</p>
          <span className="inline-flex items-center gap-1 rounded-full bg-[var(--hover-tint)] px-2 py-0.5 text-[11px] font-medium text-[var(--text-ter)]">
            {mode === 'conditions' ? <Filter className="h-2.5 w-2.5" /> : <Layers className="h-2.5 w-2.5" />}
            {mode === 'conditions' ? 'Conditions' : 'Dimensions'}
          </span>
        </div>
        {frame.description && <p className="text-xs text-[var(--text-ter)]">{frame.description}</p>}

        {mode === 'conditions' ? (
          conditions.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {conditions.map((c, i) => (
                <span key={c.id} className="inline-flex items-center gap-1.5">
                  <span className="border border-primary-200 bg-primary-50 px-2 py-1 text-xs text-primary-800">
                    {conditionSummary(c)}
                  </span>
                  {i < conditions.length - 1 && (
                    <span className="text-[10px] font-semibold text-[var(--text-ter)]">{joinWord}</span>
                  )}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-xs text-[var(--mustard)] italic">No conditions — track is incomplete</span>
          )
        ) : frame.dimensions.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5">
            {frame.dimensions.map((dim, i) => (
              <span key={dim.key} className="inline-flex items-center gap-1.5">
                <span className="rounded-full border border-primary-200 bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-800">
                  {dim.label}
                </span>
                {i < frame.dimensions.length - 1 && (
                  <MoveRight className="h-3 w-3 text-[var(--text-ter)] shrink-0" />
                )}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-xs text-[var(--mustard)] italic">No dimensions — track is incomplete</span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0 mt-0.5">
        <button type="button" onClick={() => onEdit(frame.id)}
          className="rounded p-1.5 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-sec)]" aria-label="Edit track">
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => onDelete(frame.id)}
          className="rounded p-1.5 text-[var(--text-ter)] hover:bg-[var(--coral-dim)] hover:text-[var(--coral)]" aria-label="Delete track">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── Frame editor form ─────────────────────────────────────────────────────────

type FrameFormValues = Pick<
  CustomBaselineDef,
  'name' | 'dimensions' | 'description' | 'definitionMode' | 'conditions' | 'match'
>;

interface FrameFormProps {
  initialFrame?: Partial<FrameFormValues>;
  allDimensions: FrameAttributeMeta[];
  onSave: (values: FrameFormValues) => void;
  onCancel: () => void;
  saveLabel?: string;
}

function FrameForm({ initialFrame, allDimensions, onSave, onCancel, saveLabel = 'Add frame' }: FrameFormProps) {
  const [name, setName] = useState(initialFrame?.name ?? '');
  const [description, setDescription] = useState(initialFrame?.description ?? '');
  const [definitionMode, setDefinitionMode] = useState<FrameDefinitionMode>(
    initialFrame?.definitionMode ?? 'dimensions'
  );
  const [dimensions, setDimensions] = useState<FrameDimension[]>(initialFrame?.dimensions ?? []);
  const [conditions, setConditions] = useState<FrameCondition[]>(initialFrame?.conditions ?? []);
  const [match, setMatch] = useState<'all' | 'any'>(initialFrame?.match ?? 'all');

  const completeConditions = useMemo(() => conditions.filter(isConditionComplete), [conditions]);
  const isValid =
    name.trim().length > 0 &&
    (definitionMode === 'dimensions' ? dimensions.length > 0 : completeConditions.length > 0);

  // ── Dimension handlers ──
  const usedKeys = useMemo(() => new Set(dimensions.map((d) => d.key)), [dimensions]);
  const available = useMemo(
    () => allDimensions.filter((d) => !usedKeys.has(d.key)),
    [allDimensions, usedKeys]
  );
  const handleAddDimension = (dim: FrameAttributeMeta) =>
    setDimensions((prev) => [...prev, { key: dim.key, label: dim.label }]);
  const handleRemoveDimension = (key: string) =>
    setDimensions((prev) => prev.filter((d) => d.key !== key));
  const handleMoveUp = (idx: number) => {
    if (idx <= 0) return;
    setDimensions((prev) => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  };
  const handleMoveDown = (idx: number) => {
    setDimensions((prev) => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  };

  // ── Condition handlers ──
  const handleAddCondition = () => {
    const first = allDimensions[0];
    if (!first) return;
    const operator = operatorsForType(first.type)[0];
    setConditions((prev) => [
      ...prev,
      { id: generateId(), key: first.key, label: first.label, operator },
    ]);
  };
  const handleChangeCondition = (id: string, next: FrameCondition) =>
    setConditions((prev) => prev.map((c) => (c.id === id ? next : c)));
  const handleRemoveCondition = (id: string) =>
    setConditions((prev) => prev.filter((c) => c.id !== id));

  const handleSave = () => {
    if (!isValid) return;
    if (definitionMode === 'dimensions') {
      onSave({ name: name.trim(), definitionMode, dimensions, description: description.trim() || undefined });
    } else {
      onSave({
        name: name.trim(),
        definitionMode,
        dimensions,
        conditions: completeConditions,
        match,
        description: description.trim() || undefined,
      });
    }
  };

  return (
    <div className="border border-primary-200 bg-primary-50/30 p-5 space-y-5">
      {/* Name + description */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
            Track name <span className="text-[var(--coral)]">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Strategic Work"
            className="block w-full rounded border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm text-[var(--text-pri)] focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
            Description <span className="text-[var(--text-ter)] font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short context about this track"
            className="block w-full rounded border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm text-[var(--text-pri)] focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Definition mode toggle */}
      <div className="space-y-2">
        <label className="block text-xs font-medium text-[var(--text-sec)]">How is membership defined?</label>
        <div className="inline-flex border border-[var(--rule)] bg-[var(--bg-raised)] p-0.5 text-sm">
          <button
            type="button"
            onClick={() => setDefinitionMode('dimensions')}
            className={[
              'inline-flex items-center gap-1.5 rounded px-3 py-1.5 font-medium transition-colors',
              definitionMode === 'dimensions' ? 'bg-primary-600 text-white' : 'text-[var(--text-sec)] hover:bg-[var(--surface)]',
            ].join(' ')}
          >
            <Layers className="h-3.5 w-3.5" />
            Group by dimensions
          </button>
          <button
            type="button"
            onClick={() => setDefinitionMode('conditions')}
            className={[
              'inline-flex items-center gap-1.5 rounded px-3 py-1.5 font-medium transition-colors',
              definitionMode === 'conditions' ? 'bg-primary-600 text-white' : 'text-[var(--text-sec)] hover:bg-[var(--surface)]',
            ].join(' ')}
          >
            <Filter className="h-3.5 w-3.5" />
            Include by conditions
          </button>
        </div>
      </div>

      {/* ── Dimensions editor ── */}
      {definitionMode === 'dimensions' && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] mb-0.5">
              Dimensions <span className="text-[var(--coral)]">*</span>
            </label>
            <p className="text-xs text-[var(--text-ter)]">
              Items with the same values across all dimensions belong to this track's comparability lane.
              Order determines how lanes are labeled and grouped.
            </p>
          </div>

          {dimensions.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              {dimensions.map((dim, idx) => (
                <span key={dim.key} className="inline-flex items-center gap-2">
                  <DimensionChip
                    dim={dim}
                    index={idx}
                    total={dimensions.length}
                    onMoveUp={() => handleMoveUp(idx)}
                    onMoveDown={() => handleMoveDown(idx)}
                    onRemove={() => handleRemoveDimension(dim.key)}
                  />
                  {idx < dimensions.length - 1 && (
                    <MoveRight className="h-3.5 w-3.5 text-[var(--text-ter)] shrink-0" />
                  )}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-[var(--mustard)]">Add at least one dimension to define the comparability boundary.</p>
          )}

          <DimensionPicker available={available} onAdd={handleAddDimension} />
        </div>
      )}

      {/* ── Conditions editor ── */}
      {definitionMode === 'conditions' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-0.5">
                Inclusion conditions <span className="text-[var(--coral)]">*</span>
              </label>
              <p className="text-xs text-[var(--text-ter)]">
                An item is included when its fields satisfy these conditions — the things that must be true.
              </p>
            </div>
            {conditions.length > 1 && (
              <div className="inline-flex items-center gap-1.5 text-xs text-[var(--text-sec)]">
                <span>Match</span>
                <select
                  value={match}
                  onChange={(e) => setMatch(e.target.value as 'all' | 'any')}
                  className="rounded border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1 text-xs text-[var(--text-pri)] focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                >
                  <option value="all">all conditions (AND)</option>
                  <option value="any">any condition (OR)</option>
                </select>
              </div>
            )}
          </div>

          {conditions.length > 0 ? (
            <div className="space-y-2">
              {conditions.map((c) => (
                <ConditionRow
                  key={c.id}
                  condition={c}
                  allDimensions={allDimensions}
                  onChange={(next) => handleChangeCondition(c.id, next)}
                  onRemove={() => handleRemoveCondition(c.id)}
                />
              ))}
            </div>
          ) : (
            <p className="text-xs text-[var(--mustard)]">Add at least one condition for items to match this frame.</p>
          )}

          <button
            type="button"
            onClick={handleAddCondition}
            disabled={allDimensions.length === 0}
            className="inline-flex items-center gap-1.5 rounded border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus className="h-3.5 w-3.5" />
            Add condition
          </button>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1 border-t border-primary-100">
        <button
          type="button"
          onClick={handleSave}
          disabled={!isValid}
          className="inline-flex items-center gap-1.5 rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Check className="h-3.5 w-3.5" />
          {saveLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 rounded border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-1.5 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)]"
        >
          <X className="h-3.5 w-3.5" />
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function TrackSettings() {
  const getCurrentWorkspace = useTenantStore((s) => s.getCurrentWorkspace);
  const updateWorkspace = useTenantStore((s) => s.updateWorkspace);
  const workspace = getCurrentWorkspace();

  const attributes = useAttributesStore((s) => s.attributes);
  const fetchAttributes = useAttributesStore((s) => s.fetchFromSupabase);

  useEffect(() => { fetchAttributes(); }, [fetchAttributes]);

  // Build the full dimension picklist: system fields + workspace attributes
  const allDimensions = useMemo<FrameAttributeMeta[]>(() => {
    const fromAttributes: FrameAttributeMeta[] = attributes
      .filter((a) => !a.isCustomField) // scoring (system) attributes — always eligible
      .map((a) => ({ key: a.key, label: a.label, type: (a.type as AttributeType) ?? 'text', options: a.options }));

    const fromCustomFields: FrameAttributeMeta[] = attributes
      .filter((a) => a.isCustomField && a.enabled !== false && a.usable_as_criterion === true)
      .map((a) => ({ key: a.key, label: a.label, type: (a.type as AttributeType) ?? 'text', options: a.options }));

    // System fields first, then scoring attributes, then custom fields.
    // Deduplicate by key (system fields shadow any attribute with the same key).
    const seen = new Set<string>();
    const result: FrameAttributeMeta[] = [];
    for (const dim of [...SYSTEM_DIMENSIONS, ...fromAttributes, ...fromCustomFields]) {
      if (!seen.has(dim.key)) {
        seen.add(dim.key);
        result.push(dim);
      }
    }
    return result;
  }, [attributes]);

  const policy: BaselineTemplatePolicy = workspace?.baselineTemplatePolicy ?? { mode: 'category-by-altitude' };
  const currentMode = policy.mode ?? 'category-by-altitude';
  const customFrames: CustomBaselineDef[] = policy.customFrames ?? [];

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // ── Persist ───────────────────────────────────────────────────────────────

  const savePolicy = useCallback(
    (next: BaselineTemplatePolicy) => {
      if (!workspace?.id) return;
      updateWorkspace(workspace.id, { baselineTemplatePolicy: next });
    },
    [workspace?.id, updateWorkspace]
  );

  const handleModeChange = useCallback(
    (mode: BaselineTemplatePolicy['mode']) => savePolicy({ ...policy, mode }),
    [policy, savePolicy]
  );

  const handleAddFrame = useCallback(
    (values: FrameFormValues) => {
      const newFrame: CustomBaselineDef = { id: generateId(), ...values };
      savePolicy({ ...policy, mode: 'custom', customFrames: [...customFrames, newFrame] });
      setShowAddForm(false);
    },
    [policy, customFrames, savePolicy]
  );

  const handleEditFrame = useCallback(
    (id: string, values: FrameFormValues) => {
      const next = customFrames.map((f) => (f.id === id ? { ...f, ...values } : f));
      savePolicy({ ...policy, customFrames: next });
      setEditingId(null);
    },
    [policy, customFrames, savePolicy]
  );

  const handleDeleteFrame = useCallback(
    (id: string) => savePolicy({ ...policy, customFrames: customFrames.filter((f) => f.id !== id) }),
    [policy, customFrames, savePolicy]
  );

  const handleMoveUp = useCallback(
    (id: string) => {
      const idx = customFrames.findIndex((f) => f.id === id);
      if (idx <= 0) return;
      const next = [...customFrames];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      savePolicy({ ...policy, customFrames: next });
    },
    [policy, customFrames, savePolicy]
  );

  const handleMoveDown = useCallback(
    (id: string) => {
      const idx = customFrames.findIndex((f) => f.id === id);
      if (idx < 0 || idx >= customFrames.length - 1) return;
      const next = [...customFrames];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      savePolicy({ ...policy, customFrames: next });
    },
    [policy, customFrames, savePolicy]
  );

  // ── Guard ─────────────────────────────────────────────────────────────────

  if (!workspace) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <p className="text-sm text-[var(--text-ter)]">No workspace selected.</p>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <PageHeader
        icon={<Layers className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Tracks"
        subtitle="Define what makes items comparable within a cycle. Each custom track is a named lane built either by grouping on shared attribute values or by requiring a set of conditions to be true."
      />

      {/* ── Mode selector ─────────────────────────────────────────────────── */}
      <section className="border border-[var(--rule)] bg-[var(--bg-raised)] p-6 shadow-sm space-y-4">
        <div>
          <h2 className="text-base font-semibold text-[var(--text-pri)]">Track definition mode</h2>
          <p className="mt-1 text-sm text-[var(--text-ter)]">
            Choose how tracks are generated when a cycle is created.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {/* Category by Altitude */}
          <button
            type="button"
            onClick={() => handleModeChange('category-by-altitude')}
            className={[
              'flex items-start gap-3  border p-4 text-left transition-colors',
              currentMode === 'category-by-altitude'
                ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--rule)] hover:bg-[var(--surface)]',
            ].join(' ')}
          >
            <span className={[
              'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
              currentMode === 'category-by-altitude' ? 'bg-primary-100 text-primary-700' : 'bg-[var(--hover-tint)] text-[var(--text-ter)]',
            ].join(' ')}>
              <Layers className="h-3.5 w-3.5" />
            </span>
            <span className="space-y-1">
              <span className={[
                'block text-sm font-medium',
                currentMode === 'category-by-altitude' ? 'text-primary-800' : 'text-[var(--text-pri)]',
              ].join(' ')}>
                Item Category by Altitude
                <span className="ml-2 rounded bg-[var(--hover-tint)] px-1.5 py-0.5 text-xs font-normal text-[var(--text-ter)]">default</span>
              </span>
              <span className={[
                'block text-xs leading-relaxed',
                currentMode === 'category-by-altitude' ? 'text-primary-700' : 'text-[var(--text-ter)]',
              ].join(' ')}>
                Tracks are auto-generated from the unique combinations of Item Category
                and Altitude across a cycle's items.
              </span>
            </span>
          </button>

          {/* Custom */}
          <button
            type="button"
            onClick={() => handleModeChange('custom')}
            className={[
              'flex items-start gap-3  border p-4 text-left transition-colors',
              currentMode === 'custom'
                ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--rule)] hover:bg-[var(--surface)]',
            ].join(' ')}
          >
            <span className={[
              'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
              currentMode === 'custom' ? 'bg-primary-100 text-primary-700' : 'bg-[var(--hover-tint)] text-[var(--text-ter)]',
            ].join(' ')}>
              <GripVertical className="h-3.5 w-3.5" />
            </span>
            <span className="space-y-1">
              <span className={[
                'block text-sm font-medium',
                currentMode === 'custom' ? 'text-primary-800' : 'text-[var(--text-pri)]',
              ].join(' ')}>
                Custom tracks
              </span>
              <span className={[
                'block text-xs leading-relaxed',
                currentMode === 'custom' ? 'text-primary-700' : 'text-[var(--text-ter)]',
              ].join(' ')}>
                Define named frames. Each is built either by grouping on shared attribute
                values, or by requiring 1..n conditions to be true for inclusion.
              </span>
            </span>
          </button>
        </div>

        {currentMode === 'category-by-altitude' && (
          <div className="flex items-start gap-2 border border-[var(--slate)] bg-[var(--slate-dim)] px-3 py-2.5 text-sm text-[var(--text-sec)]">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Two dimensions define comparability: <strong>Item Category</strong> and <strong>Altitude</strong>.
              A new track lane is created for each unique (category, altitude) pair found in the cycle's items.
              Switch to Custom tracks to use different or additional attributes, or to define tracks by conditions.
            </span>
          </div>
        )}
      </section>

      {/* ── Custom tracks list ─────────────────────────────────────────────── */}
      {currentMode === 'custom' && (
        <section className="border border-[var(--rule)] bg-[var(--bg-raised)] p-6 shadow-sm space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-[var(--text-pri)]">Defined tracks</h2>
              <p className="mt-0.5 text-sm text-[var(--text-ter)]">
                Each track is a named comparability lane. An item belongs to a track when it shares the
                track's dimension values, or satisfies the track's inclusion conditions.
              </p>
            </div>
            {!showAddForm && (
              <button
                type="button"
                onClick={() => { setShowAddForm(true); setEditingId(null); }}
                className="shrink-0 inline-flex items-center gap-1.5 rounded border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]"
              >
                <Plus className="h-3.5 w-3.5" />
                New track
              </button>
            )}
          </div>

          {/* Add form */}
          {showAddForm && (
            <FrameForm
              allDimensions={allDimensions}
              onSave={handleAddFrame}
              onCancel={() => setShowAddForm(false)}
              saveLabel="Add track"
            />
          )}

          {/* Empty state */}
          {customFrames.length === 0 && !showAddForm && (
            <div className="border border-dashed border-[var(--rule)] py-10 text-center">
              <Layers className="mx-auto h-8 w-8 text-[var(--text-ter)] mb-2" />
              <p className="text-sm text-[var(--text-ter)]">No tracks defined yet.</p>
              <p className="text-xs text-[var(--text-ter)] mt-1">
                Click "New track" to define the first Track for this workspace.
              </p>
            </div>
          )}

          {/* Frame list */}
          {customFrames.length > 0 && (
            <div className="space-y-2">
              {customFrames.map((frame, idx) =>
                editingId === frame.id ? (
                  <div key={frame.id} className="border border-[var(--rule)] bg-[var(--surface)] p-3">
                    <FrameForm
                      initialFrame={{
                        name: frame.name,
                        dimensions: frame.dimensions,
                        description: frame.description,
                        definitionMode: definitionModeOf(frame),
                        conditions: frame.conditions,
                        match: frame.match,
                      }}
                      allDimensions={allDimensions}
                      onSave={(values) => handleEditFrame(frame.id, values)}
                      onCancel={() => setEditingId(null)}
                      saveLabel="Save changes"
                    />
                  </div>
                ) : (
                  <FrameRow
                    key={frame.id}
                    frame={frame}
                    onEdit={(id) => { setEditingId(id); setShowAddForm(false); }}
                    onDelete={handleDeleteFrame}
                    onMoveUp={handleMoveUp}
                    onMoveDown={handleMoveDown}
                    isFirst={idx === 0}
                    isLast={idx === customFrames.length - 1}
                  />
                )
              )}
            </div>
          )}
        </section>
      )}

      {/* ── Info note ─────────────────────────────────────────────────────── */}
      <section className="border border-[var(--rule)] bg-[var(--surface)] p-4 text-sm text-[var(--text-ter)] leading-relaxed space-y-2">
        <p>
          <strong className="text-[var(--text-sec)]">How tracks work.</strong>{' '}
          A Track defines a comparability lane within a cycle.
          A <em>dimension</em> track groups items that share the same values across all of its
          dimensions — e.g. <em>Division → Item Category</em> creates a separate lane for each
          unique (Division, Category) pair. A <em>condition</em> track includes any item whose fields
          satisfy its conditions — e.g. <em>Item Category is Initiative AND Altitude ≥ 3 AND Division is Sales</em>.
        </p>
        <p>
          This policy applies when new cycles are created. Existing cycle tracks are not retroactively
          changed when this setting is updated.
        </p>
      </section>
    </div>
  );
}

export default TrackSettings;
